import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, MessageCircle, ChevronRight, ScanLine, Calculator, Globe, Percent } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import { WHATSAPP_LINK } from '../utils/constants';
import { STATE_LIST } from '../data/statePages';

const GROUPS = [
  {
    title: 'Receipt & Invoice Scanners',
    icon: ScanLine,
    items: [
      { label: 'Invoice & Receipt Scanner', desc: 'Scan invoices and receipts and turn them into bookkeeping records.', to: '/invoice-scanner' },
      { label: 'Receipt to Excel', desc: 'Convert receipts into an Excel spreadsheet.', to: '/receipt-to-excel' },
      { label: 'Receipt to CSV', desc: 'Convert receipts into a CSV file.', to: '/receipt-to-csv' },
      { label: 'Invoice to Excel', desc: 'Extract invoice data to Excel.', to: '/invoice-to-excel' },
      { label: 'PDF Invoice to Excel Converter', desc: 'Convert PDF invoices to Excel.', to: '/convert-pdf-invoice-to-excel' },
      { label: 'Scan Receipts to PDF', desc: 'Scan receipts and save them as a PDF.', to: '/scan-receipts-to-pdf' },
      { label: 'Schedule C Receipt Scanner', desc: 'Scan receipts and organize them for Schedule C.', to: '/schedule-c-receipt-scanner' },
      { label: 'VAT Receipt Scanner', desc: 'Scan receipts and capture the VAT details.', to: '/vat-receipt-scanner' },
    ],
  },
  {
    title: 'US Tax Calculators',
    icon: Calculator,
    items: [
      { label: '1099 Tax Calculator', desc: 'Estimate the tax on 1099 income.', to: '/tools/1099-tax-calculator' },
        ...STATE_LIST.map(s => ({ label: 'Self-Employment Tax in ' + s.state, desc: 'Estimate self-employment tax in ' + s.state + '.', to: '/' + s.slug })),
    ],
  },
  {
    title: 'International Tax Calculators',
    icon: Globe,
    items: [
      { label: 'UK Self Assessment', desc: 'Estimate UK Self Assessment tax for the self-employed.', to: '/tools/uk-self-assessment-tax-calculator' },
      { label: 'Canada Self-Employed', desc: 'Estimate Canadian tax for self-employed income.', to: '/tools/canada-self-employed-tax-calculator' },
      { label: 'Germany Self-Employed', desc: 'Estimate German tax for self-employed income.', to: '/tools/germany-self-employed-tax-calculator' },
      { label: 'Spain Autónomo', desc: 'Estimate Spanish autónomo tax and contributions.', to: '/tools/spain-autonomo-tax-calculator' },
      { label: 'France Auto-Entrepreneur', desc: 'Estimate French auto-entrepreneur tax and contributions.', to: '/tools/france-auto-entrepreneur-tax-calculator' },
      { label: 'Australia Sole Trader', desc: 'Estimate Australian tax for sole traders.', to: '/tools/australia-sole-trader-tax-calculator' },
      { label: 'New Zealand Sole Trader', desc: 'Estimate New Zealand tax for sole traders.', to: '/tools/new-zealand-sole-trader-tax-calculator' },
    ],
  },
  {
    title: 'VAT Calculators',
    icon: Percent,
    items: [
      { label: 'UAE VAT Calculator', desc: 'Work out VAT for the UAE.', to: '/tools/uae-vat-calculator' },
      { label: 'Saudi Arabia VAT Calculator', desc: 'Work out VAT for Saudi Arabia.', to: '/tools/saudi-arabia-vat-calculator' },
      { label: 'Australia GST Calculator', desc: 'Work out GST for Australia.', to: '/tools/australia-gst-calculator' },
      { label: 'New Zealand GST Calculator', desc: 'Work out GST for New Zealand.', to: '/tools/new-zealand-gst-calculator' },
    ],
  },
];

const GUIDES = [
  { label: 'Self-employed tax in Scotland', to: '/self-employed-tax-scotland' },
  { label: 'Freelancer tax in Germany', to: '/freelancer-tax-germany' },
  { label: 'ZZP tax in the Netherlands', to: '/zzp-tax-netherlands' },
  { label: 'Autónomo tax in Spain', to: '/autonomo-tax-spain' },
  { label: 'Auto-entrepreneur tax in France', to: '/auto-entrepreneur-tax-france' },
  { label: 'Freelancer tax in Italy', to: '/freelancer-tax-italy' },
  { label: 'Freelancer tax in Portugal', to: '/freelancer-tax-portugal' },
  { label: 'Self-employed tax in Belgium', to: '/self-employed-tax-belgium' },
  { label: 'Freelancer tax in Switzerland', to: '/freelancer-tax-switzerland' },
  { label: 'Self-employed tax in Poland', to: '/self-employed-tax-poland' },
  { label: 'Self-employed tax in Ireland', to: '/self-employed-tax-ireland' },
];

export default function Tools() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="tools-page">
      <Seo
        title="Free Bookkeeping Tools & Tax Calculators | MoneyFeed"
        description="Free receipt and invoice scanners, self-employment tax calculators for the US, UK, Canada, Germany, Spain, France, Australia and New Zealand, and VAT calculators for the UAE and Saudi Arabia from MoneyFeed."
        path="/tools"
        robots="index,follow"
      />
      <LandingHeader />

      <nav className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="tools-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#064E3B] font-semibold">Tools</span>
      </nav>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">Free Tools & Calculators</h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto mb-4">
          <strong>MoneyFeed is the WhatsApp tool for freelancers and small businesses.</strong> Use these free scanners and tax calculators to turn receipts into spreadsheets and estimate what you owe, then keep your books on WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="tools-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="tools-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-10">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <div className="flex items-center gap-2 mb-4">
              <g.icon className="h-5 w-5 text-[#84CC16]" />
              <h2 className="text-2xl font-bold text-[#064E3B]">{g.title}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {g.items.map((i) => (
                <Link key={i.label} to={i.to} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg hover:border-[#84CC16] border border-transparent transition-all">
                  <h3 className="font-bold text-[#064E3B] mb-1 flex items-center gap-1">{i.label} <ArrowRight className="h-3.5 w-3.5 opacity-50" /></h3>
                  <p className="text-gray-600 text-sm">{i.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-10">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-4">Tax guides by country</h2>
        <div className="flex flex-wrap gap-3">
          {GUIDES.map((g) => (
            <Link key={g.to} to={g.to} className="bg-white rounded-full shadow px-4 py-2 text-sm font-semibold text-[#064E3B] hover:border-[#84CC16] border border-transparent transition-all">{g.label}</Link>
          ))}
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/about-moneyfeed" className="hover:text-[#064E3B]">About</Link>
          <Link to="/supported-countries" className="hover:text-[#064E3B]">Supported Countries</Link>
          <Link to="/tax-support" className="hover:text-[#064E3B]">Tax Support</Link>
          <Link to="/security" className="hover:text-[#064E3B]">Security</Link>
        </div>
      </footer>
    </div>
  );
}
